import React, { useEffect, useState, useMemo, ReactNode } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Linking, View } from 'react-native';
import ReCAPTCHA from 'react-google-recaptcha';
import { Text } from 'react-native-paper';

import { GuestService } from '@luminate/luminate-ts-sdk';
import { OneTimePasswordVerification, Header40 } from '@luminate/luminate-native-components';

import { useAuth } from '../../contexts';
import Environment from '../../models/Environment';
import { Dialog } from '../common';
import { SCREEN_NAMES } from '../routes';

const OTP_MESSAGES = {
  INVALID: 'Invalid verification code.',
  EXCEEDED: 'Too many OTP attempts. Please try again after time has expired.',
  ERROR: `An error occurred. Please try again later. If the problem persists, please contact support.`,
  TITLE: 'One Time Password Verification',
  SUB_TITLE: 'Please complete the Captcha to continue.',
};

export const OneTimePasswordScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { validateOrderOtp } = useAuth();

  const [otpId, setOtpId] = useState<string>('');
  const [otpTtl, setOtpTtl] = useState<number>(0);
  const [otp, setOtp] = useState<string>('');
  const [otpEntryComplete, setOtpEntryComplete] = useState<boolean>(false);
  const [otpDisabled, setOtpDisabled] = useState<boolean>(false);
  const [enableOtpTimer, setEnableOtpTimer] = useState<boolean>(false);
  const [showResend, setShowResend] = useState<boolean>(false);
  const [disableResend, setDisableResend] = useState<boolean>(false);
  const [otpError, setOtpError] = useState<boolean>(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('');
  const [otpProcessing, setOtpProcessing] = useState<boolean>(false);
  const [captcha, setCaptcha] = useState<string | null>(null);

  const useUrl = useMemo(
    () => async (routePath: string) => {
      const initialUrl = await Linking.getInitialURL();
      const url = new URL(initialUrl as string);
      // @ts-ignore
      url.pathname = `${routePath}/${route.params?.transactionId}`;
      return url.href;
    },
    // @ts-ignore: extracting transactionId from route params
    [route.params?.transactionId],
  );

  useEffect(() => {
    if (captcha) {
      generateOtp();
    }
  }, [captcha]);

  useEffect(() => {
    if (otpEntryComplete) {
      validateOtpCode();
    }
  }, [otpEntryComplete]);

  const onOtpChanged = (value: string) => {
    setOtp(value);
    setOtpErrorMessage('');
    setOtpError(false);
  };

  const generateOtp = async () => {
    setOtpProcessing(true);
    try {
      const url = await useUrl('/dat/guest/order');
      // @ts-ignore: extracting transactionId from route params
      const { transactionId } = route.params;
      const guestService = GuestService.create(Environment.apiBaseUrl as string);
      const response = await guestService.generateOrderOtp(transactionId, url);
      setOtpId(response.id);
      setOtpTtl(response.ttl);
      setOtpError(false);
      setOtpEntryComplete(false);
      setOtpDisabled(false);
      setEnableOtpTimer(true);
      setShowResend(false);
    } catch (error) {
      setOtpErrorMessage(OTP_MESSAGES.INVALID);
      setOtpError(true);
    }
    setOtpProcessing(false);
  };

  const resendVerificationCode = async () => {
    await generateOtp();
  };

  const onTimerComplete = () => {
    setOtpDisabled(true);
    setEnableOtpTimer(false);
    setShowResend(true);
    setDisableResend(false);
  };

  const onOtpComplete = async () => {
    setOtpEntryComplete(true);
  };

  const validateOtpCode = async () => {
    setOtpProcessing(true);
    try {
      // @ts-ignore: extracting transactionId from route params
      const { transactionId } = route.params;
      const order = await validateOrderOtp(otpId, otp, transactionId);

      //@ts-ignore
      navigation.navigate(SCREEN_NAMES.ORDER_DETAILS, { orderId: order.id });
    } catch (error) {
      setOtpError(true);
      setOtp('');
      setOtpEntryComplete(false);

      // @ts-ignore
      switch (error.status) {
        case 429:
          setOtpErrorMessage(OTP_MESSAGES.EXCEEDED);
          setOtpDisabled(true);
          break;
        case 401:
          setOtpErrorMessage(OTP_MESSAGES.INVALID);
          break;
        default:
          setOtpErrorMessage(OTP_MESSAGES.ERROR);
      }
    }
    setOtpProcessing(false);
  };

  const getOtpSubtitle = (): ReactNode => {
    return (
      <Text>
        A message with a verification code has been sent to your email. Please do not navigate away from this page.
        Enter the code to continue.
      </Text>
    );
  };

  return (
    <Dialog style={{ alignItems: 'flex-start', marginTop: 50 }}>
      {captcha && (
        <OneTimePasswordVerification
          title={OTP_MESSAGES.TITLE}
          subtitle={getOtpSubtitle()}
          value={otp}
          error={otpError}
          errorMessage={otpErrorMessage}
          processing={otpProcessing}
          onCodeChanged={onOtpChanged}
          onCodeComplete={onOtpComplete}
          onTimerComplete={onTimerComplete}
          timerDuration={otpTtl}
          enableTimer={enableOtpTimer}
          disabled={otpDisabled || !captcha}
          showResendVerificationCode={showResend}
          disableResendVerificationCode={disableResend}
          onResendVerificationCode={resendVerificationCode}
        />
      )}
      {!captcha && (
        <View style={{ alignItems: 'center' }}>
          <Header40 style={{ marginVertical: 20 }}>{OTP_MESSAGES.TITLE}</Header40>
          <View style={{ marginVertical: 10 }}>
            <Text variant={'bodyMedium'} style={{ textAlign: 'center' }}>
              {OTP_MESSAGES.SUB_TITLE}
            </Text>
          </View>
          <ReCAPTCHA
            style={{ alignItems: 'center' }}
            sitekey={Environment.captchaSiteKey as string}
            onChange={setCaptcha}
          />
        </View>
      )}
    </Dialog>
  );
};
